import { useEffect } from "react";

/**
 * Activity shared by all tabs of this browser (localStorage), so an idle tab
 * does not log out a tab that is in use or recording.
 */
const ACTIVITY_KEY = "rkm.activityAt";
const EXPIRY_KEY = "rkm.sessionExpiresAt";
const LOGOUT_KEY = "rkm.loggedOutAt";
/** Writes to localStorage at most this often (pointermove fires a lot). */
const ACTIVITY_THROTTLE_MS = 5000;

let localActivity = 0;
let localExpiry = 0;
let loggingOut = false;
const holds = new Set<symbol>();

function readNumber(key: string): number {
  try {
    return Number(window.localStorage.getItem(key)) || 0;
  } catch {
    return 0;
  }
}

function writeNumber(key: string, value: number): void {
  try {
    window.localStorage.setItem(key, String(value));
  } catch {
    // Storage blocked: the value still applies to this tab.
  }
}

/** Records user activity (or a running recording) at `now`. */
export function markActivity(now = Date.now(), force = false): void {
  if (!force && now - localActivity < ACTIVITY_THROTTLE_MS) {
    return;
  }
  localActivity = now;
  writeNumber(ACTIVITY_KEY, now);
}

/** Last activity in any tab (ms since epoch). */
export function lastActivity(): number {
  return Math.max(localActivity, readNumber(ACTIVITY_KEY));
}

/** Stores the session expiry of the latest renewal for all tabs. */
export function shareExpiry(expiresAt: number): void {
  localExpiry = expiresAt;
  writeNumber(EXPIRY_KEY, expiresAt);
}

/** Latest known session expiry of any tab, or 0. */
export function sharedExpiry(): number {
  return Math.max(localExpiry, readNumber(EXPIRY_KEY));
}

/** Keeps the session open while held (recording, upload). */
export function holdSession(): () => void {
  const id = Symbol("hold");
  holds.add(id);
  return () => {
    holds.delete(id);
  };
}

export function isHeld(): boolean {
  return holds.size > 0;
}

/** Holds the session while `active` (recording, upload in progress). */
export function useSessionHold(active: boolean): void {
  useEffect(() => {
    if (!active) {
      return;
    }
    const release = holdSession();
    markActivity(Date.now(), true);
    return () => {
      markActivity(Date.now(), true);
      release();
    };
  }, [active]);
}

/** Set before the automatic logout navigates away (no "leave page?" dialog). */
export function beginLogout(): void {
  loggingOut = true;
}

export function isLoggingOut(): boolean {
  return loggingOut;
}

/** Tells the other tabs that this browser logged out. */
export function announceLogout(): void {
  writeNumber(LOGOUT_KEY, Date.now());
}

/** Calls `onLogout` when another tab logs out. */
export function onLogoutElsewhere(onLogout: () => void): () => void {
  const handler = (event: StorageEvent) => {
    if (event.key === LOGOUT_KEY && event.newValue) {
      onLogout();
    }
  };
  window.addEventListener("storage", handler);
  return () => window.removeEventListener("storage", handler);
}

/** Test helper: forget the state of this tab. */
export function resetActivityForTests(): void {
  localActivity = 0;
  localExpiry = 0;
  loggingOut = false;
  holds.clear();
}
